import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import "../../../assets/css/style.css";
import { useProviderForm } from "./BaseInfo/useProviderForm";
import ProveedorInfoSection from "./BaseInfo/ProveedorInfoSection";
import TransferenciasSection from "./BaseInfo/TransferenciasSection";
import RetencionesSection from "./BaseInfo/RetencionesSection";
import FormButtons from "./BaseInfo/FormButtons";

const API_URL = process.env.REACT_APP_API_URL || 'https://apiprov.conduto.com.ec';

const BaseInfo = ({ formData, setFormData, isReadOnly = false, idFormulario }) => {
  const navigate = useNavigate();
  const { type } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accionistasTotal, setAccionistasTotal] = useState(0);
  const [referenciasTotal, setReferenciasTotal] = useState(0);

  const isJuridica = type === "juridica";

  const {
    errors,
    isLoading,
    showCustomFormaPago,
    handleChange,
    handleBlur,
    handleFocus,
    validateForm,
  } = useProviderForm(formData, setFormData, isJuridica, idFormulario);

  const isValid = () => {
    const data = formData.providerInfo || {};
    const requiredFields = [
      "ruc", "razon_social", "direccion_establecimiento",
      "telefono1", "persona_contacto", "cargo",
      "email_contacto", "forma_pago", "email_transferencia",
      "tipo_cuenta", "numero_cuenta", "nombre_banco",
      "email_retencion", "provincia", "canton", "servicios_prestados",
      "contribuyente_especial", "regimen_rimpe"
    ];

    const camposCompletos = requiredFields.every(field => {
      const value = data[field]?.toString().trim();
      return value !== "" && value !== undefined && value !== null;
    });

    if (!camposCompletos) return false;

    if (referenciasTotal === 0) {
      return false;
    }

    if (isJuridica) {
      const representanteCompleto = data.representante_legal?.trim() &&
        data.cedula_representante?.trim();
      if (!representanteCompleto) return false;
      if (accionistasTotal < 25) return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    if (isReadOnly) {
      navigate(`/form/${type}/2${idFormulario ? `?id_formulario=${idFormulario}` : ''}`);
      return;
    }

    setIsSubmitting(true);
    try {
      let formaPagoFinal = formData.providerInfo.forma_pago;
      if (formaPagoFinal === "Otro") {
        formaPagoFinal = formData.providerInfo.forma_pago_otro.trim();
      }

      const payload = {
        id_formulario: idFormulario,
        ruc: formData.providerInfo.ruc,
        razon_social: formData.providerInfo.razon_social,
        razon_comercial: formData.providerInfo.razon_comercial,
        contribuyente_especial: formData.providerInfo.contribuyente_especial,
        regimen_rimpe: formData.providerInfo.regimen_rimpe,
        direccion_establecimiento: formData.providerInfo.direccion_establecimiento,
        provincia: formData.providerInfo.provincia,
        canton: formData.providerInfo.canton,
        servicios_prestados: formData.providerInfo.servicios_prestados,
        telefono1: formData.providerInfo.telefono1,
        telefono2: formData.providerInfo.telefono2,
        telefono3: formData.providerInfo.telefono3,
        persona_contacto: formData.providerInfo.persona_contacto,
        cargo: formData.providerInfo.cargo,
        email_contacto: formData.providerInfo.email_contacto,
        forma_pago: formaPagoFinal,
        forma_pago_otro: formData.providerInfo.forma_pago_otro,
        email_transferencia: formData.providerInfo.email_transferencia,
        tipo_cuenta: formData.providerInfo.tipo_cuenta,
        numero_cuenta: formData.providerInfo.numero_cuenta,
        nombre_banco: formData.providerInfo.nombre_banco,
        email_retencion: formData.providerInfo.email_retencion,
      };

      if (isJuridica) {
        payload.representante_legal = formData.providerInfo.representante_legal;
        payload.cedula_representante = formData.providerInfo.cedula_representante;
      }

      const token = localStorage.getItem("token");

      const saveRes = await fetch(`${API_URL}/api/providers/save-provider-data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!saveRes.ok) {
        const msg = await saveRes.json();
        throw new Error(msg.error || "Error al guardar");
      }

      const stepRes = await fetch(
        `${API_URL}/api/providers/update-step/${idFormulario}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify({ step: 2 })
        }
      );

      if (!stepRes.ok) {
        console.warn("No se pudo actualizar el paso, pero los datos se guardaron");
      }

      const ruta = `/form/${type}/2${idFormulario ? `?id_formulario=${idFormulario}` : ''}`;
      navigate(ruta);
    } catch (err) {
      console.error("Error al guardar:", err);
      alert(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading)
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-2">Cargando datos del proveedor...</p>
      </div>
    );

  return (
    <Container fluid className="px-0">
      <Row>
        <Col xs={12}>
          <ProveedorInfoSection
            formData={formData}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleFocus={handleFocus}
            isReadOnly={isReadOnly}
            isJuridica={isJuridica}
            showCustomFormaPago={showCustomFormaPago}
            idFormulario={idFormulario}
            setAccionistasTotal={setAccionistasTotal}
            setReferenciasTotal={setReferenciasTotal}
          />

          <TransferenciasSection
            formData={formData}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleFocus={handleFocus}
            isReadOnly={isReadOnly}
          />

          <RetencionesSection
            formData={formData}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleFocus={handleFocus}
            isReadOnly={isReadOnly}
          />

          <FormButtons
            errors={errors}
            isSubmitting={isSubmitting}
            handleSave={handleSave}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default BaseInfo;